import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Microscope } from "lucide-react";
import { resolveImage } from "../data/products";
import { useCatalog } from "../context/CatalogContext";
import { getCompoundProfile } from "../data/compoundProfiles";
import ProductImage from "../components/ProductImage";
import CompoundProfileModal from "../components/CompoundProfileModal";

/**
 * Build the flat list of "vials to show" — one card per branded photo.
 * Variant-level images get their own card; otherwise we fall back to the
 * product-level image (its default variant).
 */
const buildLookbookEntries = (products) => {
  const entries = [];
  (products || []).forEach((product) => {
    const variantImages = (product.variants || []).filter((v) => v.image);
    if (variantImages.length > 0) {
      variantImages.forEach((v) => {
        const { src, fallback } = resolveImage(product, v);
        entries.push({
          key: `${product.slug}-${v.id}`,
          slug: product.slug,
          src,
          fallback,
          alt: `${product.name} — ${v.label}`,
          name: product.name,
          detail: v.label,
        });
      });
    } else {
      const defaultVariant =
        product.variants.find((v) => v.id === product.defaultVariant) || product.variants[0];
      const { src, fallback } = resolveImage(product, defaultVariant);
      entries.push({
        key: product.slug,
        slug: product.slug,
        src,
        fallback,
        alt: product.name,
        name: product.name,
        detail: defaultVariant?.label || "",
      });
    }
  });
  return entries;
};

/**
 * One image tile with a subtle vertical parallax driven by scroll position.
 */
const LookbookTile = ({ entry, parallaxStrength = 20, index, onProfile }) => {
  const wrapperRef = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const el = wrapperRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight || 1;
        const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2);
        setOffset(Math.max(-1, Math.min(1, progress)) * parallaxStrength);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [parallaxStrength]);

  const hasProfile = !!getCompoundProfile(entry.slug);

  return (
    <figure
      ref={wrapperRef}
      data-testid={`lookbook-tile-${entry.key}`}
      className="group relative overflow-hidden rounded-[28px] bg-cream-200 shadow-[0_8px_24px_-12px_rgba(60,40,55,0.18)] hover:shadow-[0_24px_44px_-18px_rgba(60,40,55,0.28)] hover:-translate-y-1 hover:rotate-[0.4deg] transition-[transform,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform animate-fade-up"
      style={{ animationDelay: `${(index % 12) * 60}ms` }}
    >
      <div className="aspect-[4/5] overflow-hidden">
        <ProductImage
          src={entry.src}
          fallback={entry.fallback}
          alt={entry.alt}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
          style={{ transform: `translate3d(0, ${offset.toFixed(2)}px, 0)` }}
          loading="lazy"
        />
      </div>
      <figcaption className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-gradient-to-t from-charcoal-600/65 via-charcoal-600/15 to-transparent text-cream-100">
        <span className="block font-display text-lg md:text-xl font-bold tracking-tight">
          {entry.name}
        </span>
        {hasProfile && (
          <button
            type="button"
            onClick={() => onProfile(entry.slug)}
            data-testid={`lookbook-profile-btn-${entry.slug}`}
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium tracking-[0.16em] uppercase text-cream-100/85 hover:text-cream-100 bg-white/12 hover:bg-white/22 backdrop-blur-sm border border-white/22 hover:border-white/40 rounded-full px-3 py-1.5 transition-colors"
          >
            <Microscope className="w-3 h-3" strokeWidth={2.2} />
            Compound Information
          </button>
        )}
      </figcaption>
    </figure>
  );
};

const Lookbook = () => {
  const { products } = useCatalog();
  const entries = useMemo(() => buildLookbookEntries(products), [products]);
  const [activeSlug, setActiveSlug] = useState(null);
  const profile = activeSlug ? getCompoundProfile(activeSlug) : null;

  return (
    <div data-testid="lookbook-page" className="bg-cream-100 min-h-screen">
      <section className="container-x pt-16 md:pt-24 pb-12 md:pb-16">
        <div className="max-w-3xl">
          <span className="overline mb-5 inline-block">Research Library · 2026</span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-charcoal-600">
            The LuviaRx<br />Research Library
          </h1>
          <p className="mt-6 text-base text-charcoal-400 leading-relaxed max-w-xl">
            A complete visual index of every compound in our catalogue, photographed and documented to the same standard we apply in the lab.
          </p>
        </div>
      </section>

      <section className="container-x pb-24">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {entries.map((entry, i) => (
            <LookbookTile
              key={entry.key}
              entry={entry}
              index={i}
              parallaxStrength={i % 2 === 0 ? 18 : 28}
              onProfile={setActiveSlug}
            />
          ))}
        </div>
      </section>

      <section className="container-x pb-24">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-charcoal-100/60 pt-10">
          <div>
            <p className="overline mb-2">For Research Use Only</p>
            <p className="font-display text-2xl md:text-3xl font-bold text-charcoal-600 leading-tight">
              Ready to spec a compound?
            </p>
          </div>
          <Link
            to="/products"
            data-testid="lookbook-shop-catalog"
            className="btn-primary inline-flex items-center gap-2 group"
          >
            Shop Catalog
            <ArrowUpRight
              className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
              strokeWidth={2}
            />
          </Link>
        </div>
      </section>

      <CompoundProfileModal
        open={!!profile}
        onClose={() => setActiveSlug(null)}
        profile={profile}
      />
    </div>
  );
};

export default Lookbook;
